import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { useState, useEffect, useCallback } from 'react';

const API = '/api';

const TREE_TAXONS = [
  'Ольха (Alnus)', 'Берёза (Betula)', 'Лещина (Corylus)', 'Клён (Acer)',
  'Тополь (Populus)', 'Ива (Salix)', 'Сосна (Pinus)', 'Ель (Picea)',
  'Вяз (Ulmus)', 'Ясень (Fraxinus)', 'Лиственница (Larix)',
  'Липа (Tilia)', 'Можжевельник (Juniperus)', 'Дуб (Quercus)'
];

const HERB_TAXONS = [
  'Злаки (Poaceae)', 'Полынь (Artemisia)', 'Маревые (Chenopodiaceae)',
  'Подорожник (Plantago)', 'Крапивные (Urticaceae)', 'Щавель (Rumex)',
  'Астровые (Asteraceae)', 'Зонтичные (Umbelliferae)'
];

const ALL_TAXONS = [...TREE_TAXONS, ...HERB_TAXONS];

const calcLevelTree = (count) => {
  if (count > 1000) return 4;
  if (count > 100) return 3;
  if (count > 10) return 2;
  return 1;
};

const calcLevelHerb = (count) => {
  if (count > 100) return 4;
  if (count > 30) return 3;
  if (count > 10) return 2;
  return 1;
};

const getLevel = (taxon, count) => {
  if (TREE_TAXONS.includes(taxon)) return calcLevelTree(count);
  if (HERB_TAXONS.includes(taxon)) return calcLevelHerb(count);
  if (count > 100) return 4;
  if (count > 30) return 3;
  if (count > 10) return 2;
  return 1;
};

const levelInfo = (level) => {
  switch (level) {
    case 4: return { color: '#e74c3c', text: 'Высокий' };
    case 3: return { color: '#f39c12', text: 'Средний' };
    case 2: return { color: '#f1c40f', text: 'Низкий' };
    case 1: return { color: '#27ae60', text: 'Безопасный' };
    default: return { color: '#999', text: 'Нет данных' };
  }
};

const TrendArrow = ({ current, previous }) => {
  if (previous === null || previous === undefined) return null;
  if (current > previous) return <span style={{ color: '#e74c3c', marginLeft: 6 }}>↑</span>;
  if (current < previous) return <span style={{ color: '#27ae60', marginLeft: 6 }}>↓</span>;
  return <span style={{ color: '#999', marginLeft: 6 }}>→</span>;
};

// ==================== АКТУАЛЬНЫЕ ДАННЫЕ (главная) ====================
function TodayPage() {
  const [data, setData] = useState([]);
  const [prevData, setPrevData] = useState({});
  const [lastDate, setLastDate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/observations`)
      .then(res => res.json())
      .then(allData => {
        if (allData.length === 0) { setLoading(false); return; }
        const dates = [...new Set(allData.map(d => d.date))].sort().reverse();
        const latest = dates[0];
        const previous = dates[1] || null;
        setLastDate(latest);
        const latestData = allData.filter(d => d.date === latest).map(item => ({ ...item, level: getLevel(item.taxon, item.pollenCount) }));
        if (previous) {
          const prevMap = {};
          allData.filter(d => d.date === previous).forEach(d => { prevMap[d.taxon] = d.pollenCount; });
          setPrevData(prevMap);
        }
        setData(latestData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 20 }}>⏳ Загрузка...</div>;
  if (data.length === 0) return <div style={{ padding: 20 }}>Нет данных. <Link to="/admin">Добавить</Link></div>;

  const treeData = data.filter(d => TREE_TAXONS.includes(d.taxon));
  const herbData = data.filter(d => HERB_TAXONS.includes(d.taxon));

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <h1>📊 Актуальные данные</h1>
      <p style={{ color: '#666', fontSize: 16 }}>Дата: <strong>{new Date(lastDate).toLocaleDateString('ru-RU')}</strong></p>

      {treeData.length > 0 && (
        <>
          <h2 style={{ marginTop: 30 }}>🌳 Древесные растения</h2>
          <div style={{ width: '100%', height: 460 }}>
            <ResponsiveContainer>
              <BarChart data={treeData} margin={{ top: 25, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="taxon" angle={-35} textAnchor="end" height={150} interval={0} fontSize={13} fontWeight="bold" />
                <YAxis label={{ value: 'пыльц. зер./м³', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, _, props) => [`${value} пыльц. зер./м³`, levelInfo(props.payload.level).text]} />
                <Bar dataKey="pollenCount" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 12, fontWeight: 'bold', fill: '#333' }}>
                  {treeData.map(entry => <Cell key={entry.id} fill={levelInfo(entry.level).color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {herbData.length > 0 && (
        <>
          <h2 style={{ marginTop: 40 }}>🌾 Травянистые растения</h2>
          <div style={{ width: '100%', height: 400 }}>
            <ResponsiveContainer>
              <BarChart data={herbData} margin={{ top: 25, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="taxon" angle={-35} textAnchor="end" height={150} interval={0} fontSize={13} fontWeight="bold" />
                <YAxis label={{ value: 'пыльц. зер./м³', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value, _, props) => [`${value} пыльц. зер./м³`, levelInfo(props.payload.level).text]} />
                <Bar dataKey="pollenCount" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 12, fontWeight: 'bold', fill: '#333' }}>
                  {herbData.map(entry => <Cell key={entry.id} fill={levelInfo(entry.level).color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      <h2 style={{ marginTop: 40 }}>📋 Все таксоны</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', textAlign: 'center' }}>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Таксон</th>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Тип</th>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Концентрация, пыльц. зер./м³</th>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Динамика</th>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Уровень</th>
            </tr>
          </thead>
          <tbody>
            {data.map(item => (
              <tr key={item.id} style={{ textAlign: 'center' }}>
                <td style={{ padding: 10, fontWeight: 'bold', border: '1px solid #ddd' }}>{item.taxon}</td>
                <td style={{ padding: 10, border: '1px solid #ddd' }}>{TREE_TAXONS.includes(item.taxon) ? '🌳 Дерево' : '🌾 Трава'}</td>
                <td style={{ padding: 10, border: '1px solid #ddd' }}>{item.pollenCount} пыльц. зер./м³</td>
                <td style={{ padding: 10, border: '1px solid #ddd' }}>
                  <TrendArrow current={item.pollenCount} previous={prevData[item.taxon]} />
                  {prevData[item.taxon] !== undefined && (
                    <span style={{ fontSize: 14, color: '#555', marginLeft: 6 }}>было {prevData[item.taxon]}</span>
                  )}
                </td>
                <td style={{ padding: 10, border: '1px solid #ddd' }}>
                  <span style={{ background: levelInfo(item.level).color, color: 'white', padding: '5px 14px', borderRadius: 14, fontSize: 13, fontWeight: 'bold' }}>
                    {levelInfo(item.level).text}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== АРХИВ ====================
function ArchivePage() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [archiveData, setArchiveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trendTaxon, setTrendTaxon] = useState(ALL_TAXONS[0]);
  const [trendData, setTrendData] = useState(null);
  const [seasonData, setSeasonData] = useState(null);

  const loadArchive = () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    fetch(`${API}/observations?date_gte=${dateFrom}&date_lte=${dateTo}`)
      .then(res => res.json())
      .then(data => { setArchiveData(data); setLoading(false); });
  };

  const loadTrend = useCallback(() => {
    const today = new Date();
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(today.getDate() - 14);
    const from = twoWeeksAgo.toISOString().split('T')[0];
    const to = today.toISOString().split('T')[0];
    fetch(`${API}/observations?date_gte=${from}&date_lte=${to}`)
      .then(res => res.json())
      .then(all => setTrendData(all.filter(d => d.taxon === trendTaxon).sort((a, b) => a.date.localeCompare(b.date))));
  }, [trendTaxon]);

  const loadSeason = useCallback(() => {
    const year = new Date().getFullYear();
    const from = `${year}-03-01`;
    const to = `${year}-11-30`;
    fetch(`${API}/observations?date_gte=${from}&date_lte=${to}`)
      .then(res => res.json())
      .then(all => setSeasonData(all.filter(d => d.taxon === trendTaxon).sort((a, b) => a.date.localeCompare(b.date))));
  }, [trendTaxon]);

  useEffect(() => { loadTrend(); loadSeason(); }, [loadTrend, loadSeason]);

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <h1>📁 Архив наблюдений</h1>

      <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 8, marginTop: 20, marginBottom: 30 }}>
        <h3 style={{ marginTop: 0 }}>📈 Динамика концентрации (последние 2 недели)</h3>
        <select value={trendTaxon} onChange={e => setTrendTaxon(e.target.value)} style={{ padding: 6, fontSize: 14, marginBottom: 15 }}>
          {ALL_TAXONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {trendData && trendData.length > 0 ? (
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis label={{ value: 'пыльц. зер./м³', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={value => [`${value} пыльц. зер./м³`]} />
                <Legend />
                <Line type="linear" dataKey="pollenCount" name={trendTaxon} stroke="#3498db" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : <p>Нет данных за последние 2 недели</p>}
      </div>

      <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 8, marginTop: 20, marginBottom: 30 }}>
        <h3 style={{ marginTop: 0 }}>📈 Сезонная динамика (март – ноябрь {new Date().getFullYear()})</h3>
        <p style={{ fontSize: 13, color: '#666' }}>Таксон: {trendTaxon}</p>
        {seasonData && seasonData.length > 0 ? (
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={seasonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} angle={-45} textAnchor="end" height={80} />
                <YAxis label={{ value: 'пыльц. зер./м³', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={value => [`${value} пыльц. зер./м³`]} />
                <Legend />
                <Line type="linear" dataKey="pollenCount" name={trendTaxon} stroke="#e67e22" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : <p>Нет данных за сезон</p>}
      </div>

      <h3>🔍 Поиск по датам</h3>
      <div style={{ display: 'flex', gap: 15, marginTop: 10, flexWrap: 'wrap' }}>
        <label>С: <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: 6 }} /></label>
        <label>По: <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: 6 }} /></label>
        <button onClick={loadArchive} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Показать</button>
      </div>
      {loading && <p>⏳ Загрузка...</p>}
      {archiveData && archiveData.length > 0 && (
        <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 20, fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', textAlign: 'center' }}>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Дата</th>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Таксон</th>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Концентрация, пыльц. зер./м³</th>
              <th style={{ padding: 10, border: '1px solid #ddd' }}>Уровень</th>
            </tr>
          </thead>
          <tbody>
            {archiveData.map(item => (
              <tr key={item.id} style={{ textAlign: 'center' }}>
                <td style={{ padding: 10, border: '1px solid #ddd' }}>{item.date}</td>
                <td style={{ padding: 10, fontWeight: 'bold', border: '1px solid #ddd' }}>{item.taxon}</td>
                <td style={{ padding: 10, border: '1px solid #ddd' }}>{item.pollenCount} пыльц. зер./м³</td>
                <td style={{ padding: 10, border: '1px solid #ddd' }}>
                  <span style={{ background: levelInfo(getLevel(item.taxon, item.pollenCount)).color, color: 'white', padding: '4px 10px', borderRadius: 10, fontSize: 12, fontWeight: 'bold' }}>
                    {levelInfo(getLevel(item.taxon, item.pollenCount)).text}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ==================== О НАС ====================
function AboutPage() {
  const paragraphStyle = {
    textIndent: '1.5em',
    marginBottom: '1em',
    lineHeight: 1.8,
  };

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto', lineHeight: 1.8 }}>
      <h1>🌿 О проекте</h1>
      <p style={{ color: '#666', fontSize: 16 }}>
        Проект кафедры ботаники и генетики растений Пермского государственного национального исследовательского университета (ПГНИУ).
      </p>
      <hr style={{ margin: '25px 0' }} />

      <p style={paragraphStyle}>
        Начиная с 2008 года в Перми ведутся систематические аэропалинологические исследования, позволяющие изучить состав атмосферного воздуха и предупредить обострение аллергических заболеваний. За годы мониторинга был накоплен значительный объём данных, позволяющих судить о вариабельности пыления, отслеживать тенденции, прогнозировать и предотвращать проявления сезонных поллинозов населения Перми.
      </p>
      <p style={paragraphStyle}>
        Главной целью мониторинга является предупреждение населения Перми о повышении концентрации пыльцевых зёрен в атмосферном воздухе и возможном обострении симптомов поллиноза, или, иными словами, аллергии на пыльцу растений. Порог, с которого начинаются опасные концентрации для чувствительных людей, составляет всего 30 пыльцевых зёрен в кубическом метре воздуха в сутки. Для жителей Перми, чьи симптомы обостряются во время цветения растений, эти данные критически важны. Отслеживание ведется с помощью волюметрического пыльцеуловителя Lanzoni VPPS 2010, который установлен на крыше географического корпуса Пермского государственного университета по адресу Генкеля, 8.
      </p>
      <p style={paragraphStyle}>
        Пыльцеуловитель круглосуточно поддерживает постоянный поток воздуха, имитирующий скорость дыхания человека — 10 литров в минуту. Внутри пыльцеуловителя взвешенные в воздухе частицы осаждаются на специальную липкую ленту. Затем вручную, с использованием микроскопа, проводится идентификация нанесённых на стеклопрепараты и предварительно окрашенных клейких лент, содержащих пыльцевые зёрна. Определяется точная концентрация каждого таксона. Полученные данные ложатся в основу календаря пыления, помогая аллергологам назначать терапию, а аллергикам — подготовиться к возможным нежелательным реакциям и минимизировать их.
      </p>
    </div>
  );
}

// ==================== ЛОГИН ====================
function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = (e) => {
    e.preventDefault();
    fetch(`${API}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          sessionStorage.setItem('loggedIn', 'true');
          navigate('/admin');
        } else {
          setError(data.error || 'Ошибка входа');
        }
      })
      .catch(() => setError('Ошибка сервера'));
  };

  return (
    <div style={{ padding: 40, maxWidth: 400, margin: '50px auto', background: '#f8f9fa', borderRadius: 8 }}>
      <h2>🔐 Вход</h2>
      {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 4, marginBottom: 15 }}>{error}</div>}
      <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input type="text" placeholder="Логин" value={username} onChange={e => setUsername(e.target.value)} style={{ padding: 10, fontSize: 14, borderRadius: 4, border: '1px solid #ccc' }} />
        <input type="password" placeholder="Пароль" value={password} onChange={e => setPassword(e.target.value)} style={{ padding: 10, fontSize: 14, borderRadius: 4, border: '1px solid #ccc' }} />
        <button type="submit" style={{ padding: 10, background: '#3498db', color: 'white', border: 'none', borderRadius: 4, fontSize: 15, cursor: 'pointer', fontWeight: 'bold' }}>Войти</button>
      </form>
    </div>
  );
}

// ==================== АДМИНИСТРАЦИЯ ====================
function AdminPage() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [taxon, setTaxon] = useState(ALL_TAXONS[0]);
  const [pollenCount, setPollenCount] = useState('');
  const [message, setMessage] = useState('');
  const [allObservations, setAllObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editCount, setEditCount] = useState('');
  const navigate = useNavigate();
  const loggedIn = sessionStorage.getItem('loggedIn');

  useEffect(() => {
    if (!loggedIn) navigate('/login');
  }, [loggedIn, navigate]);

  const loadAll = useCallback(() => {
    setLoading(true);
    fetch(`${API}/observations`)
      .then(res => res.json())
      .then(data => { setAllObservations(data); setLoading(false); });
  }, []);

  useEffect(() => { if (loggedIn) loadAll(); }, [loggedIn, loadAll]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetch(`${API}/observations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, taxon, pollenCount: Number(pollenCount) }),
    })
      .then(() => { setMessage('✅ Добавлено'); setPollenCount(''); loadAll(); setTimeout(() => setMessage(''), 2000); });
  };

  const handleDelete = (id) => {
    if (!window.confirm('Удалить?')) return;
    fetch(`${API}/observations/${id}`, { method: 'DELETE' })
      .then(() => { setMessage('🗑️ Удалено'); loadAll(); });
  };

  const startEdit = (obs) => { setEditingId(obs.id); setEditCount(obs.pollenCount); };
  const cancelEdit = () => { setEditingId(null); setEditCount(''); };

  const saveEdit = () => {
    const obs = allObservations.find(o => o.id === editingId);
    fetch(`${API}/observations/${editingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: obs.date, taxon: obs.taxon, pollenCount: Number(editCount) }),
    })
      .then(() => { setMessage('✏️ Обновлено'); cancelEdit(); loadAll(); });
  };

  const logout = () => { sessionStorage.clear(); navigate('/'); };
  if (!loggedIn) return null;

  return (
    <div style={{ padding: 20, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>🔧 Администрация</h1>
        <button onClick={logout} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold' }}>🚪 Выйти</button>
      </div>
      {message && <div style={{ padding: 10, borderRadius: 6, marginBottom: 15, background: '#d4edda', color: '#155724' }}>{message}</div>}
      <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 8, marginBottom: 30 }}>
        <h3>➕ Добавить наблюдение</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label>Дата: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ padding: 7, fontSize: 14, borderRadius: 4, border: '1px solid #ccc' }} /></label>
          <label>Таксон: <select value={taxon} onChange={e => setTaxon(e.target.value)} style={{ padding: 7, fontSize: 14, borderRadius: 4, border: '1px solid #ccc' }}>{ALL_TAXONS.map(t => <option key={t} value={t}>{t}</option>)}</select></label>
          <label>Пыльцевых зёрен/м³: <input type="number" value={pollenCount} onChange={e => setPollenCount(e.target.value)} placeholder="150" min="0" required style={{ width: 80, padding: 7, fontSize: 14, borderRadius: 4, border: '1px solid #ccc' }} /></label>
          <button type="submit" style={{ padding: '8px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 'bold' }}>💾 Сохранить</button>
        </form>
      </div>
      <h3>📋 Все записи ({allObservations.length})</h3>
      {loading ? <p>⏳ Загрузка...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#e9ecef', textAlign: 'center' }}>
                <th style={{ padding: 8, border: '1px solid #ddd' }}>ID</th>
                <th style={{ padding: 8, border: '1px solid #ddd' }}>Дата</th>
                <th style={{ padding: 8, border: '1px solid #ddd' }}>Таксон</th>
                <th style={{ padding: 8, border: '1px solid #ddd' }}>Пыльц. зер./м³</th>
                <th style={{ padding: 8, border: '1px solid #ddd' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {allObservations.map(obs => (
                <tr key={obs.id} style={{ background: editingId === obs.id ? '#fffde7' : 'white', textAlign: 'center' }}>
                  {editingId === obs.id ? (
                    <>
                      <td style={{ padding: 6, border: '1px solid #ddd' }}>{obs.id}</td>
                      <td style={{ padding: 6, border: '1px solid #ddd' }}>{obs.date}</td>
                      <td style={{ padding: 6, border: '1px solid #ddd', fontWeight: 'bold' }}>{obs.taxon}</td>
                      <td style={{ padding: 6, border: '1px solid #ddd' }}><input type="number" value={editCount} onChange={e => setEditCount(e.target.value)} style={{ width: 70, padding: 4, fontSize: 12, textAlign: 'center' }} /></td>
                      <td style={{ padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                        <button onClick={saveEdit} style={{ padding: '4px 10px', background: '#28a745', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>💾</button>
                        <button onClick={cancelEdit} style={{ padding: '4px 10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 12 }}>Отмена</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: 6, border: '1px solid #ddd' }}>{obs.id}</td>
                      <td style={{ padding: 6, border: '1px solid #ddd' }}>{obs.date}</td>
                      <td style={{ padding: 6, border: '1px solid #ddd', fontWeight: 'bold' }}>{obs.taxon}</td>
                      <td style={{ padding: 6, border: '1px solid #ddd' }}>{obs.pollenCount}</td>
                      <td style={{ padding: 6, border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                        <button onClick={() => startEdit(obs)} style={{ padding: '4px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>✏️</button>
                        <button onClick={() => handleDelete(obs.id)} style={{ padding: '4px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ==================== ПРИЛОЖЕНИЕ ====================
function App() {
  return (
    <BrowserRouter>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <header style={{
          background: '#f8f9fa', borderBottom: '2px solid #3498db',
          padding: '10px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 24 }}>🌿</span>
            <span style={{ fontWeight: 'bold', fontSize: 18, color: '#333' }}>Мониторинг пыльцы. Город Пермь</span>
          </div>
          <nav style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <Link to="/" style={linkStyle}>📊 Актуальные данные</Link>
            <Link to="/archive" style={linkStyle}>📁 Архив</Link>
            <Link to="/about" style={linkStyle}>🌿 О нас</Link>
            <Link to="/admin" title="Администрация" style={{ textDecoration: 'none', fontSize: 20 }}>⚙️</Link>
          </nav>
        </header>
        <main style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<TodayPage />} />
            <Route path="/archive" element={<ArchivePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </main>
        <footer style={{
          background: '#f8f9fa', borderTop: '1px solid #ddd',
          padding: '20px', textAlign: 'center', fontSize: 13, color: '#666'
        }}>
          <p>© {new Date().getFullYear()} Пермский государственный национальный исследовательский университет</p>
          <p>Кафедра ботаники и генетики растений | <a href="mailto:pollen@psu.ru" style={{ color: '#3498db' }}>pollen@psu.ru</a></p>
          <p style={{ fontSize: 12 }}>г. Пермь, ул. Букирева, 15</p>
        </footer>
      </div>
    </BrowserRouter>
  );
}

const linkStyle = {
  textDecoration: 'none', fontWeight: 'bold', color: '#333', fontSize: 15
};

export default App;
